import { BestOfRingVideo } from '@amzn/ring-neighbors-contentful-gql-schema';
import { Box, Collapse, Flex, Stack } from '@amzn/ring-ui-react-components';
import {
  ChangeEvent,
  CSSProperties,
  LegacyRef,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ReactPlayerProps } from 'react-player';
import { useHistory } from 'react-router-dom';
import { useNeighborsWebClickEventStreamDispatcher } from 'src/components/App/hooks';
import { useAuth } from 'src/context';
import { Duration, Volume } from 'src/features/BestOfRing/components';
import { Thumbnail } from 'src/features/BestOfRing/components/Media/Thumbnail';
import { ShareButton } from 'src/features/BestOfRing/components/ShareButton';
import { useHasWebViewUserAgent } from 'src/hooks';
import { AppRoutes } from 'src/routes';
import { BackButton } from 'src/shared/components/BackButton';
import { Buffering } from 'src/shared/components/VideoPlayer/Buffering';
import {
  PauseButton,
  PlayButton,
} from 'src/shared/components/VideoPlayer/VideoPlayerButton';
import { useIsMobile, useShareButtonAction } from 'src/shared/hooks';
import { UploadYourVideoButton } from '../components/UploadYourVideoButton';

const DELAY_HIDE_CONTROLS = 3000;

interface VideoThumbnailItemProps extends ReactPlayerProps {
  video: BestOfRingVideo;
  isScrolling: boolean;
  style?: CSSProperties;
  isBuffering: boolean;
  hasStarted: boolean;
  play: () => void;
  pause: () => void;
  toggleMute: () => void;
  updatePlayed: (value: number) => void;
  seekTo: (value: number) => void;
  innerRef?: LegacyRef<HTMLDivElement>;
  onSubmitYourVideo: () => void;
}

export const VideoThumbnailItem = ({
  video,
  isScrolling,
  style,
  play,
  pause,
  playing,
  played,
  loaded,
  duration,
  isBuffering,
  hasStarted,
  toggleMute,
  updatePlayed,
  seekTo,
  muted,
  innerRef,
  onSubmitYourVideo,
}: VideoThumbnailItemProps) => {
  const isWebview = useHasWebViewUserAgent();
  const history = useHistory();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const [showControls, setShowControls] = useState(false);
  const isPlayingRef = useRef<boolean>();
  const isSeekingRef = useRef<boolean>();
  const [seeking, setSeeking] = useState<boolean>(false);
  const isMobile = useIsMobile();
  const { isAuthenticated } = useAuth();

  const shouldShowSubmitButton = useMemo(
    () => isAuthenticated || isWebview,
    [isWebview, isAuthenticated],
  );

  useEffect(
    () =>
      //clear timeouts to prevent executing actions after the component is unmounted
      () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      },
    [],
  );

  useEffect(() => {
    isPlayingRef.current = playing;
  }, [playing]);

  useEffect(() => {
    isSeekingRef.current = seeking;
  }, [seeking]);

  const share = useShareButtonAction({
    shareUrl:
      window.location.origin + AppRoutes.BestOfRing + '/' + video.sys.id,
    title: document.title,
    referringItem: 'nhWeb_bestOfRingVideo',
  });

  const dispatchClick =
    useNeighborsWebClickEventStreamDispatcher('nhWeb_bestOfRing');

  const onShare = () => {
    if (dispatchClick) {
      dispatchClick('nhWeb_share', [video?.name!], {
        itemType: 'button',
      });
    }
    share();
  };

  const onBack = () => {
    if (dispatchClick) {
      dispatchClick('nhWeb_back', [video?.sys.id!], {
        itemType: 'button',
      });
    }
    history.push('/best-of-ring/');
  };

  const togglePlayPause = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    // Do not play/pause the video if user tapping/clicking on the video controls/buttons
    if (
      target &&
      (target.closest('#video-controls') ||
        target.closest('#share-controls') ||
        target.closest('#back-button') ||
        target.closest('#submit-video'))
    )
      return;
    setShowControls(true);
    if (playing) {
      pause();
    } else {
      play();
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(
      () => setShowControls(false),
      DELAY_HIDE_CONTROLS,
    );
  };

  const handleToggleMute = (e: MouseEvent<HTMLButtonElement>): void => {
    toggleMute();
    e.stopPropagation();
  };

  const handleSeekChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    updatePlayed(parseFloat(e.target.value));
  };

  const handleSeekMouseDown = () => {
    setSeeking(true);
  };

  const handleSeekMouseUp = (e: ChangeEvent<HTMLInputElement>) => {
    setSeeking(false);
    seekTo(parseFloat(e.target.value));
  };

  useEffect(() => {
    if (isBuffering && !isScrolling) {
      setShowControls(true);
    } else if (!isBuffering) {
      setShowControls(false);
    }
  }, [isBuffering, isScrolling]);

  const { title, orientation, thumbnail, mobileThumbnail } = video;

  return (
    <Box
      id="thumbnail-item"
      ref={innerRef}
      style={style}
      position="relative"
      height="100dvh"
      data-testid="video-parent"
      width="100%"
      onClick={togglePlayPause}
    >
      <Box
        height="100%"
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        data-testid="thumnail-wrapper"
        bgColor="rgba(18, 18, 18, 1)"
        style={{ display: isScrolling || !hasStarted ? '' : 'none' }}
      >
        <Thumbnail
          src={(isMobile ? mobileThumbnail?.url : thumbnail?.url) as string}
          orientation={orientation?.toLowerCase() as 'portrait' | 'landscape'}
        />
      </Box>
      <Box style={{ display: isScrolling ? 'none' : '' }}>
        <Box
          position="absolute"
          top="0"
          p={4}
          left="0"
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          bg="linear-gradient(178.09deg, rgba(0, 0, 0, 0.7) 1.61%, rgba(0, 0, 0, 0) 17.2%), 
              linear-gradient(178.09deg, rgba(0, 0, 0, 0.05) 80.61%, rgba(0, 0, 0, 0.7) 97.34%)"
        >
          <Box
            display="flex"
            height="30px"
            justifyContent={
              !isWebview && shouldShowSubmitButton
                ? 'space-between'
                : shouldShowSubmitButton && isWebview
                ? 'flex-end'
                : 'flex-start'
            }
          >
            {!isWebview && (
              <Box>
                <BackButton
                  id="back-button"
                  onClick={onBack}
                  position="relative"
                  color="white"
                  top="-5px"
                  data-testid="back-button"
                />
              </Box>
            )}
            <Box>
              {shouldShowSubmitButton && (
                <UploadYourVideoButton
                  id="submit-video"
                  height="unset"
                  variant="ghost"
                  border="1px solid white"
                  color="white"
                  onClick={onSubmitYourVideo}
                  _hover={{
                    color: 'white',
                  }}
                  _active={{
                    backgroundColor: 'transparent',
                    color: 'white',
                  }}
                  position="relative"
                />
              )}
            </Box>
          </Box>
          <Collapse in={showControls}>
            <Box display="grid" placeItems="center">
              {!isBuffering && playing && <PauseButton />}
              {!isBuffering && !playing && <PlayButton />}
              {isBuffering && (
                <Buffering size="3em" data-testid="buffering-icon" />
              )}
            </Box>
          </Collapse>
          <Box display="flex" flexDirection="column" pb="24px">
            <Box display="flex" flexDirection="column">
              <Box
                display="flex"
                alignItems="flex-end"
                flexDirection="row"
                width="100%"
              >
                <Box width="100%" flexGrow={1}>
                  <Box
                    textStyle="body-normal-medium"
                    color="white"
                    data-testid="video-title"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      WebkitLineClamp: 2,
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {title}
                  </Box>
                </Box>
                <Box id="share-controls" pl="24px">
                  <Stack>
                    <ShareButton
                      onClick={onShare}
                      data-testid="share-control"
                    />
                  </Stack>
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems="flex-end"
                flexDirection="row"
                width="100%"
                id="video-controls"
              >
                {!isScrolling && (
                  <Box width="100%" flexGrow={1} pt="12px">
                    <Flex width="100%" position="relative">
                      <Volume
                        data-testid="volume-control"
                        onClick={handleToggleMute}
                        isMuted={muted}
                      />
                      <Duration
                        data-testid="duration-control"
                        played={played}
                        duration={duration}
                        loaded={loaded}
                        handleSeekChange={handleSeekChange}
                        handleSeekMouseDown={handleSeekMouseDown}
                        handleSeekMouseUp={handleSeekMouseUp}
                      />
                    </Flex>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
